import { useLocation } from 'react-router-dom';
import { useHeader } from '../../hooks/useHeader';
import { getPageTitle } from '../../utils/pageTitle';
import { HeaderNotifications } from './HeaderNotifications';
import { HeaderSearch } from './HeaderSearch';
import { HeaderUserMenu } from './HeaderUserMenu';

interface HeaderProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
}

/**
 * Sits over the content column only, matching the comp -- not full-width above
 * the sidebar as niraxobid.md section 10.1 describes. That also keeps branding
 * in one place (the sidebar's org row) instead of showing it twice.
 *
 * Owns the single useHeader() call; HeaderUserMenu takes its state as props.
 */
export function Header({ isCollapsed, onToggleSidebar }: HeaderProps) {
  const { pathname } = useLocation();
  /*
    The bell owns its own data (useNotifications): the counts, the open tab's list and both marks.
    `notificationsEnabled` is only about whether there is a signed-in user to fetch for -- the feed itself
    is every role's, since the server scopes it to whoever is asking.
  */
  const { notificationsEnabled, isUserMenuOpen, toggleUserMenu, closeUserMenu } = useHeader();

  return (
    <header className="app-header" aria-label="Application header">
      <div className="header__left">
        <button
          type="button"
          className="header__toggle"
          onClick={onToggleSidebar}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-controls="app-sidebar"
          aria-expanded={!isCollapsed}
        >
          {/*
            toggle.png's sidebar-panel glyph (2026-10-14): a framed panel with its left rail, and a chevron that points
            the way a click moves the rail -- in to collapse it, out to bring it back.
          */}
          <svg
            className="header__toggle-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18" />
            <path d={isCollapsed ? 'm14 9 3 3-3 3' : 'm16 15-3-3 3-3'} />
          </svg>
        </button>

        <h1 className="header__page-title">{getPageTitle(pathname)}</h1>

      </div>

      <div className="header__right">
        <HeaderSearch />
        <HeaderNotifications enabled={notificationsEnabled} />
        <HeaderUserMenu
          isOpen={isUserMenuOpen}
          onToggle={toggleUserMenu}
          onClose={closeUserMenu}
        />
      </div>
    </header>
  );
}
